import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatCr, GATES, GATE_LABELS } from "@/lib/utils-ros";
import { Gauge } from "lucide-react";

/**
 * Partner-only benchmark dashboard — the caller's own average deal size and time-per-gate
 * next to the company-wide average, so a partner can see how they compare without ever
 * seeing another partner's name, another client's name, or an individual deal.
 */
export function PartnerBenchmarks() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/opportunities/benchmarks").then(({ data }) => {
      if (!cancelled) setData(data);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="rounded-[22px] bg-[#07539A] px-5 md:px-7 py-6 text-white/70 text-[13px]">
        Loading your benchmarks…
      </div>
    );
  }
  if (!data?.mine) return null;

  return (
    <div className="rounded-[22px] overflow-hidden shadow-[0_10px_28px_rgba(7,83,154,0.10)]" data-testid="partner-benchmarks">
      <div className="bg-[#07539A] px-5 md:px-7 pt-5 pb-6">
        <div className="flex items-center gap-2 mb-4">
          <Gauge size={15} className="text-white/70" />
          <div className="text-[13px] font-medium text-white">How you compare</div>
          <div className="ml-auto text-[10px] text-white/50">{data.mine.deal_count} of your deals · {data.company_wide.deal_count} company-wide</div>
        </div>
        <div className="grid grid-cols-2 gap-4 md:gap-6">
          <BenchmarkTile label="Avg deal size — you" value={formatCr(data.mine.avg_deal_size_cr)} accent />
          <BenchmarkTile label="Avg deal size — company-wide" value={formatCr(data.company_wide.avg_deal_size_cr)} />
        </div>
      </div>
      <div className="bg-white px-5 md:px-7 py-5 border-x border-b border-[#C9DCE6] rounded-b-[22px]">
        <div className="text-[9px] md:text-[10px] uppercase tracking-widest text-[#78909C] mb-3">Avg days per gate</div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {GATES.map((gate, i) => (
            <div key={gate} className="rounded-xl border border-[#D7E8F0] bg-[#F8FCFE] px-3 py-2.5">
              <div className="text-[9px] text-[#78909C] truncate">G{i + 1} · {GATE_LABELS[gate]}</div>
              <div className="mt-1.5 flex items-baseline gap-2">
                <span className="font-display text-base tabular-nums text-[#07539A]">
                  {data.mine.avg_days_per_gate[gate] ?? "—"}
                </span>
                <span className="text-[9px] text-[#9AAAB3] tabular-nums">
                  vs {data.company_wide.avg_days_per_gate[gate] ?? "—"}d
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BenchmarkTile({ label, value, accent = false }) {
  return (
    <div>
      <div className="text-[9px] md:text-[10px] uppercase tracking-widest text-white/60">{label}</div>
      <div className={`font-display text-xl md:text-2xl tabular-nums mt-1 ${accent ? "!text-[#F6A63B]" : "!text-white"}`}>{value}</div>
    </div>
  );
}
